// ============================================================================
// TENANT SELECTOR
// ============================================================================
// Reusable dropdown for selecting a tenant. Used by all tenant-scoped screens.
// Fetches tenant summaries and renders as a SyutSelect dropdown.

import React, { useMemo } from 'react';
import { SyutSelect } from 'components/ui';
import { useTenantSummaries } from 'hooks/api';

// ============================================================================
// TYPES
// ============================================================================

interface TenantSelectorProps {
  /** Currently selected tenant ID */
  value: string;
  /** Callback when tenant selection changes */
  onChange: (tenantId: string) => void;
  /** Label text (defaults to "Tenant") */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether to allow clearing the selection */
  isClearable?: boolean;
  /** Error message */
  error?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

const TenantSelector: React.FC<TenantSelectorProps> = ({
  value,
  onChange,
  label = 'Tenant',
  placeholder = 'Select tenant...',
  size = 'sm',
  disabled = false,
  isClearable = true,
  error,
}) => {
  const { data: tenants, loading } = useTenantSummaries();

  const options = useMemo(
    () => tenants.map(t => ({
      value: t.tenantId,
      label: `${t.tenantCode} — ${t.tenantName}`,
    })),
    [tenants],
  );

  return (
    <SyutSelect
      label={label}
      options={options}
      value={value}
      onChange={(val: string | string[] | null) => onChange((val as string) || '')}
      placeholder={loading ? 'Loading tenants...' : placeholder}
      size={size}
      isDisabled={disabled || loading}
      isClearable={isClearable}
      isSearchable
      error={error}
    />
  );
};

export default TenantSelector;
